import React, { useState } from "react";
import ReactDOM from 'react-dom';
import { createOrganization } from "../../services/DonationService";
import { Button } from '../../components/Button';

interface NewOrganizationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (org: { name: string; url: string }) => void;
}

const NewOrganizationModal: React.FC<NewOrganizationModalProps> = ({
    open,
    onClose,
    onConfirm,
}) => {
    const [org, setOrg] = useState({ name: "", url: "" });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!open) return null;

    const handleConfirm = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await createOrganization(org);
            onConfirm(data);
            setOrg({ name: "", url: "" });
            onClose();
        } catch (err: any) {
            setError(err.message || "Failed to create organization");
        } finally {
            setLoading(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-white/50 dark:border-white/10 w-full max-w-xl">
                <div className="flex justify-between items-center border-b dark:border-white/10 px-8 py-6">
                    <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">New organization</h2>
                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-3xl"
                    >
                        &times;
                    </Button>
                </div>
                <div className="px-8 py-6">
                    {error && (
                        <div className="mb-4 p-4 text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/20 rounded">
                            {error}
                        </div>
                    )}
                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">Name</label>
                        <input
                            className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded px-3 py-2 w-full"
                            placeholder="Organization name"
                            value={org.name}
                            onChange={e => setOrg(o => ({ ...o, name: e.target.value }))}
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-gray-700 dark:text-gray-300 mb-1">URL</label>
                        <input
                            className="border border-gray-300 dark:border-white/10 dark:bg-slate-800 dark:text-gray-100 rounded px-3 py-2 w-full"
                            placeholder="Organization URL"
                            value={org.url}
                            onChange={e => setOrg(o => ({ ...o, url: e.target.value }))}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 px-8 py-6 border-t dark:border-white/10">
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleConfirm}
                        disabled={!org.name || loading}
                    >
                        {loading ? "Creating..." : "Confirm"}
                    </Button>
                </div>
            </div>
        </div>
    ,
    document.body
  );
};

export default NewOrganizationModal;